/**
 * <bb-pdp-single-matcha> — quantity-break product page
 *
 * Handles bundle selection (1/2/3 bags + custom amount), purchase options
 * (one-time / subscription), projected price display, countdown/delivery date and
 * syncs the hidden form inputs (quantity, selling_plan) for Shopify's native cart.
 *
 * Discount is tiered by quantity: 2 bags -> discount2, 3+ bags -> discount3.
 * The real discount is applied by a Shopify automatic discount; here it is only displayed.
 * All user-facing strings come from data-* attributes (localized server-side via locale files).
 */
class BbPdpSingleMatcha extends HTMLElement {
  connectedCallback() {
    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;          // cents
    this.unitFullPrice = parseInt(this.dataset.unitFullPrice, 10) || this.basePrice; // UVP if set, else regular price
    this.discount2 = (parseFloat(this.dataset.discount2) || 0) / 100;
    this.discount3 = (parseFloat(this.dataset.discount3) || 0) / 100;
    this.subscriptionDiscount = (parseFloat(this.dataset.subscriptionDiscount) || 0) / 100;
    this.portions = parseInt(this.dataset.portions, 10) || 1;
    this.customMin = parseInt(this.dataset.customMin, 10) || 4;
    this.moneyFormat = this.dataset.moneyFormat || 'CHF {{amount}}';
    this.sellingPlan = this.dataset.sellingPlan || null;
    this.locale = this.dataset.locale || 'en';
    this.i18n = {
      hours: this.dataset.i18nHours || 'hrs',
      minutes: this.dataset.i18nMinutes || 'min',
      perServing: this.dataset.i18nPerServing || '',
      perServingLabel: this.dataset.i18nPerServingLabel || '',
      youSave: this.dataset.i18nYouSave || '',
    };

    this.state = { bundle: 1, customQty: this.customMin, mode: 'onetime', added: false };

    this.qtyInput = this.querySelector('[data-bb="quantity-input"]');
    this.planInput = this.querySelector('[data-bb="sellingplan-input"]');
    this.planSelect = this.querySelector('[data-bb="plan-select"]');

    this.bindEvents();
    this.render();
    this.startCountdown();
    this.renderDelivery();
  }

  disconnectedCallback() {
    if (this._iv) clearInterval(this._iv);
    if (this._addedTimer) clearTimeout(this._addedTimer);
    if (this._onGiftQuantity) document.removeEventListener('bb:gift-request-quantity', this._onGiftQuantity);
  }

  bindEvents() {
    // Bundle cards
    this.querySelectorAll('[data-bb="card"]').forEach((card) => {
      card.addEventListener('click', () => {
        this.state.bundle = parseInt(card.dataset.qty, 10);
        this.render();
      });
    });

    // Custom amount
    const custom = this.querySelector('[data-bb="card-custom"]');
    if (custom) {
      custom.addEventListener('click', (e) => {
        if (e.target.closest('[data-bb="custom-inc"]') || e.target.closest('[data-bb="custom-dec"]')) return;
        this.state.bundle = 'custom';
        this.render();
      });
      const inc = this.querySelector('[data-bb="custom-inc"]');
      const dec = this.querySelector('[data-bb="custom-dec"]');
      if (inc) inc.addEventListener('click', () => { this.state.bundle = 'custom'; this.state.customQty += 1; this.render(); });
      if (dec) dec.addEventListener('click', () => { this.state.customQty = Math.max(this.customMin, this.state.customQty - 1); this.render(); });
    }

    // Components outside this element (e.g. bb-gift-offer) can ask for a quantity
    this._onGiftQuantity = (e) => {
      const qty = e.detail && e.detail.quantity;
      if (!qty) return;
      this.state.bundle = qty > 3 ? 'custom' : qty;
      if (qty > 3) this.state.customQty = qty;
      this.render();
    };
    document.addEventListener('bb:gift-request-quantity', this._onGiftQuantity);

    // Purchase options
    const optOneTime = this.querySelector('[data-bb="opt-onetime"]');
    const optSubscription = this.querySelector('[data-bb="opt-subscription"]');
    if (optOneTime) optOneTime.addEventListener('click', () => { this.state.mode = 'onetime'; this.render(); });
    if (optSubscription) optSubscription.addEventListener('click', () => { this.state.mode = 'subscription'; this.render(); });
    if (this.planSelect) this.planSelect.addEventListener('change', () => this.render());

    // Added state via theme cart events
    document.addEventListener('variant:add', () => this.flashAdded());
    document.addEventListener('cart:change', () => this.flashAdded());
    document.addEventListener('cart:refresh', () => this.flashAdded());
    const form = document.getElementById(this.dataset.form);
    if (form) form.addEventListener('submit', () => { this._submitted = true; });
  }

  // ---- price helpers ----
  qty() { return this.state.bundle === 'custom' ? this.state.customQty : this.state.bundle; }

  discountForQty(n) {
    if (n >= 3) return this.discount3;
    if (n === 2) return this.discount2;
    return 0;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat
      .replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
  }

  bundleTotal(n) { return n * this.basePrice * (1 - this.discountForQty(n)); }

  // ---- render ----
  render() {
    const n = this.qty();
    const isCustom = this.state.bundle === 'custom';
    const isSubscription = this.state.mode === 'subscription' && this.sellingPlan;

    // Cards
    this.querySelectorAll('[data-bb="card"]').forEach((card) => {
      const c = parseInt(card.dataset.qty, 10);
      const sel = !isCustom && this.state.bundle === c;
      card.classList.toggle('is-selected', sel);
      const check = card.querySelector('[data-bb="check"]');
      if (check) check.hidden = !sel;
      const disc = this.discountForQty(c);
      const priceEl = card.querySelector('[data-bb="card-price"]');
      const struckEl = card.querySelector('[data-bb="card-struck"]');
      const perServingEl = card.querySelector('[data-bb="card-perserving"]');
      if (priceEl) priceEl.textContent = this.formatMoney(c * this.basePrice * (1 - disc));
      if (struckEl) struckEl.textContent = disc > 0 ? this.formatMoney(c * this.basePrice) : '';
      if (perServingEl) perServingEl.textContent = this.formatMoney(this.basePrice * (1 - disc) / this.portions) + ' ' + this.i18n.perServing;
    });

    // Custom row
    const customRow = this.querySelector('[data-bb="card-custom"]');
    if (customRow) {
      customRow.classList.toggle('is-selected', isCustom);
      this.setCheck(customRow.querySelector('[data-bb="check-custom"]'), isCustom);
      const controls = customRow.querySelector('[data-bb="custom-controls"]');
      const chevron = customRow.querySelector('[data-bb="custom-chevron"]');
      if (controls) controls.hidden = !isCustom;
      if (chevron) chevron.style.display = isCustom ? 'none' : '';
      const qtyEl = customRow.querySelector('[data-bb="custom-qty"]');
      if (qtyEl) qtyEl.textContent = this.state.customQty;
    }

    // Purchase options
    const bundleTotal = this.bundleTotal(n);
    const optOneTime = this.querySelector('[data-bb="opt-onetime"]');
    const optSubscription = this.querySelector('[data-bb="opt-subscription"]');
    if (optOneTime) {
      optOneTime.classList.toggle('is-selected', !isSubscription);
      this.setCheck(optOneTime.querySelector('[data-bb="check-onetime"]'), !isSubscription);
      const p = optOneTime.querySelector('[data-bb="onetime-price"]');
      if (p) p.textContent = this.formatMoney(bundleTotal);
    }
    if (optSubscription) {
      optSubscription.classList.toggle('is-selected', isSubscription);
      this.setCheck(optSubscription.querySelector('[data-bb="check-subscription"]'), isSubscription);
      const struck = optSubscription.querySelector('[data-bb="sub-struck"]');
      const price = optSubscription.querySelector('[data-bb="sub-price"]');
      if (struck) struck.textContent = this.formatMoney(bundleTotal);
      if (price) price.textContent = this.formatMoney(bundleTotal * (1 - this.subscriptionDiscount));
    }

    const finalTotal = isSubscription ? bundleTotal * (1 - this.subscriptionDiscount) : bundleTotal;
    const fullTotal = n * this.unitFullPrice;

    // Headline price: total for the current selection, with struck price + savings badge whenever a
    // quantity/subscription discount (or the product's UVP) makes it cheaper than the full price.
    const headlinePrice = this.querySelector('[data-bb="headline-price"]');
    const headlineStruck = this.querySelector('[data-bb="headline-struck"]');
    const headlineSave = this.querySelector('[data-bb="headline-save"]');
    const headlinePerServing = this.querySelector('[data-bb="headline-perserving"]');
    const saving = fullTotal - finalTotal;
    const hasSaving = saving > 1;
    if (headlinePrice) headlinePrice.textContent = this.formatMoney(finalTotal);
    if (headlineStruck) {
      headlineStruck.hidden = !hasSaving;
      headlineStruck.textContent = this.formatMoney(fullTotal);
    }
    if (headlineSave) {
      headlineSave.hidden = !hasSaving;
      headlineSave.textContent = this.i18n.youSave.replace('__amount__', this.formatMoney(saving));
    }
    if (headlinePerServing) {
      headlinePerServing.textContent = '≈ ' + this.formatMoney(finalTotal / n / this.portions) + ' ' + this.i18n.perServingLabel;
    }

    // Let loosely coupled components (bb-gift-offer) follow the selected quantity
    document.dispatchEvent(new CustomEvent('bb:quantity-change', { detail: { quantity: n } }));

    // Sticky bar
    const stickyTotal = this.querySelector('[data-bb="sticky-total"]');
    const stickyStruck = this.querySelector('[data-bb="sticky-struck"]');
    if (stickyTotal) stickyTotal.textContent = this.formatMoney(finalTotal);
    if (stickyStruck) {
      stickyStruck.hidden = !hasSaving;
      stickyStruck.textContent = this.formatMoney(fullTotal);
    }

    // Subscription rhythm dropdown only when subscribed
    const planWrap = this.querySelector('[data-bb="plan-wrap"]');
    if (planWrap) planWrap.hidden = !isSubscription;

    // Sync hidden form inputs
    if (this.qtyInput) this.qtyInput.value = n;
    if (this.planInput) {
      if (isSubscription) {
        this.planInput.value = (this.planSelect && this.planSelect.value) || this.sellingPlan;
        this.planInput.disabled = false;
      } else {
        this.planInput.disabled = true;
      }
    }
  }

  setCheck(el, on) {
    if (!el) return;
    el.classList.toggle('is-on', on);
    el.innerHTML = on ? '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' : '';
  }

  flashAdded() {
    if (!this._submitted) return;
    this._submitted = false;
    this.toggleAdded(true);
    if (this._addedTimer) clearTimeout(this._addedTimer);
    this._addedTimer = setTimeout(() => this.toggleAdded(false), 1900);
  }

  toggleAdded(on) {
    [
      ['[data-bb="cta-label"]', '[data-bb="cta-added"]'],
      ['[data-bb="sticky-cta-label"]', '[data-bb="sticky-cta-added"]'],
    ].forEach(([labelSel, addedSel]) => {
      const label = this.querySelector(labelSel);
      const added = this.querySelector(addedSel);
      if (label) label.hidden = on;
      if (added) added.hidden = !on;
    });
  }

  // ---- countdown / delivery ----
  startCountdown() {
    const el = this.querySelector('[data-bb="countdown"]');
    if (!el) return;
    const cutoffHour = parseInt(this.dataset.cutoffHour, 10) || 18;
    const tick = () => {
      const now = new Date();
      const target = new Date(now);
      target.setHours(cutoffHour, 0, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);
      const diff = Math.max(0, Math.floor((target - now) / 1000));
      const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60);
      el.textContent = (h > 0 ? h + ' ' + this.i18n.hours + ' ' : '') + m + ' ' + this.i18n.minutes;
    };
    tick();
    this._iv = setInterval(tick, 20000);
  }

  renderDelivery() {
    const el = this.querySelector('[data-bb="delivery"]');
    if (!el) return;
    const d = new Date();
    let added = 0;
    while (added < 3) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) added++; }
    try {
      el.textContent = new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: 'numeric', month: 'short' }).format(d);
    } catch (e) {
      el.textContent = d.toLocaleDateString();
    }
  }
}

if (!customElements.get('bb-pdp-single-matcha')) {
  customElements.define('bb-pdp-single-matcha', BbPdpSingleMatcha);
}
